import { useEffect, useState } from 'react'
import { Button, Text } from '../../atoms'
import { Modal } from '../../molecules'
import {
  Viewer,
  Worker,
  SpecialZoomLevel,
  ScrollMode,
  PageChangeEvent,
  DocumentLoadEvent
} from '@react-pdf-viewer/core'
import '@react-pdf-viewer/core/lib/styles/index.css'
import useSetFocusById from '../../../hooks/useSetFocus'
import ControlHighlight from '../KeyboardControls/ControlHighlight'
import { useKeyboardControls } from '../../../contexts/KeyboardControlsContext'
import KeyboardControl from '../KeyboardControls/KeyboardControl'
import { HighlightEffect } from '../../../enums/KeyboardControls'
import ContactLinks from '../ContactLinks/ContactLinks'

interface TermsModalProps {
  pdfUrl: string
  handleAcceptTerms: (setIsSubmitting: React.Dispatch<boolean>) => void
}

export enum FocusableElement {
  TextTerms = 'text-terms',
  ButtonAgree = 'button-agree',
  LinkContact = 'link-contact',
}

const TermsModal: React.FC<TermsModalProps> = ({ pdfUrl, handleAcceptTerms }) => {
  const [numPages, setNumPages] = useState(0)
  const [currentPage, setCurrentPage] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [acceptTermsButtonDisabled, setAcceptTermsButtonDisabled] = useState(true)
  const [setFocusById] = useSetFocusById()
  const keyboardControls = useKeyboardControls()

  const onDocumentLoad = (e: DocumentLoadEvent): void => {
    setNumPages(e.doc.numPages)
  }

  const onPageChange = (e: PageChangeEvent): void => {
    setCurrentPage(e.currentPage)
  }

  useEffect(() => {
    setFocusById(FocusableElement.TextTerms)
  }, [setFocusById])

  useEffect(() => {
    if (numPages > 0) {
      if (currentPage + 1 === numPages) {
        setAcceptTermsButtonDisabled(false)
      }
    }
  }, [currentPage, numPages])

  return (
    <Modal tall>
      <Text bold className='text-[26px]' text='Privacy & Terms of Use' />
      <ControlHighlight
        active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.TextTerms)}
        useSpan
        highlightEffect={HighlightEffect.TextTermsOutline}
        data-testid={`highlight-${FocusableElement.TextTerms}`}
        className='h-full w-full overflow-auto mt-[16px]'
      >
        <div className='h-full w-full overflow-auto'>

          <KeyboardControl>
            <div
              id={FocusableElement.TextTerms}
              tabIndex={-1}
              data-testid={FocusableElement.TextTerms}
            >
              <Worker workerUrl='https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js'>
                <Viewer
                  fileUrl={pdfUrl}
                  onDocumentLoad={onDocumentLoad}
                  onPageChange={onPageChange}
                  defaultScale={SpecialZoomLevel.PageWidth}
                  scrollMode={ScrollMode.Vertical}
                />
              </Worker>
            </div>
          </KeyboardControl>

        </div>
      </ControlHighlight>

      <div className='mt-[22px]'>
        <ControlHighlight
          active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonAgree)}
          highlightEffect={HighlightEffect.ButtonHexOutline}
          data-testid={`highlight-${FocusableElement.ButtonAgree}`}
        >
          <KeyboardControl>
            <Button
              id={FocusableElement.ButtonAgree}
              data-testid={FocusableElement.ButtonAgree}
              tabIndex={0}
              className='w-[356px] h-[86px] diamond-btn font-bold flex justify-center items-center'
              onClick={() => handleAcceptTerms(setIsSubmitting)}
              loading={isSubmitting}
              disabled={acceptTermsButtonDisabled}
            >
              <Text bold className='text-[26px] text-black' text='Agree + Continue' />
            </Button>
          </KeyboardControl>
        </ControlHighlight>
      </div>
      <ContactLinks
        id={FocusableElement.LinkContact}
      />
    </Modal>
  )
}

export default TermsModal
